import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";

const formatter = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

// il valore può già arrivare formattato dall'API (es. "45 giorni")
const formatValue = (value) =>
  typeof value === "number" ? formatter.format(value) : value;

// oltre questa lunghezza il valore va a capo sotto l'etichetta
const MAX_INLINE_VALUE_LENGTH = 30;

// informativa e riepilogo costi sono oggetti ({} se vuoti) che possono
// contenere un array dataset di righe { label, value }
const toRows = (section) =>
  (Array.isArray(section?.dataset) ? section.dataset : []).filter(
    (row) =>
      row?.label &&
      row.value !== null &&
      row.value !== undefined &&
      row.value !== "",
  );

// la garanzia apre il dettaglio solo se informativa o riepilogo costi
// del dataCollector hanno righe nel dataset
export const getGaranziaDetails = (garanzia) => {
  const dataCollector = garanzia?.dataCollector;
  const informativa = toRows(dataCollector?.informativa);
  const costi = toRows(dataCollector?.riepilogoCosti);
  if (!informativa.length && !costi.length) return null;
  return {
    informativa,
    costi,
    costiLabel: dataCollector.riepilogoCosti?.label || "Riepilogo costi",
    costiTotale: dataCollector.riepilogoCosti?.totalAmount ?? "",
  };
};

export const GaranziaNome = ({ garanzia, onOpen }) =>
  getGaranziaDetails(garanzia) ? (
    <Link
      component="button"
      type="button"
      underline="hover"
      onClick={() => onOpen(garanzia)}
      sx={{ fontSize: "inherit", fontWeight: 600, textAlign: "left" }}
    >
      {garanzia.nome}
    </Link>
  ) : (
    <Typography>{garanzia.nome}</Typography>
  );

const Sezione = ({ title, rows, total, type }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    {type === "table" ? (
      <Table size="small">
        <TableBody>
          {rows.map(({ label, value }) => {
            const formatted = formatValue(value);
            const isLong =
              String(formatted ?? "").length > MAX_INLINE_VALUE_LENGTH;
            return (
              <TableRow
                key={label}
                sx={{
                  display: "flex",
                  flexDirection: isLong ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isLong ? "stretch" : "center",
                  gap: isLong ? 0 : 2,
                }}
              >
                <TableCell sx={{ borderBottom: 0, py: 0.75, pl: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {label}
                  </Typography>
                </TableCell>
                <TableCell
                  align={isLong ? "left" : "right"}
                  sx={{
                    borderBottom: 0,
                    py: 0.75,
                    pr: 0,
                    pl: isLong ? 3 : undefined,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {formatted}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
          {total !== null && total !== undefined && total !== "" && (
            <TableRow sx={{ bgcolor: "action.selected" }}>
              <TableCell sx={{ py: 1 }}>
                <Typography variant="body2" fontWeight={800}>
                  Totale
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ py: 1 }}>
                <Typography variant="body2" fontWeight={800}>
                  {formatValue(Number(total))}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    ) : (
      <Typography variant="body2" color="text.secondary" mt={1} pl={3}>
        {rows}
      </Typography>
    )}
  </Box>
);

const GaranziaDrawer = ({ garanzia, open, onClose }) => {
  const details = getGaranziaDetails(garanzia);
  return (
    <Drawer
      anchor="right"
      open={open && !!details}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
    >
      {details && (
        <Box p={4} display="flex" flexDirection="column" gap={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            gap={2}
          >
            <Box>
              <Typography variant="h4">{garanzia.nome}</Typography>
              {garanzia.incluso === 1 && (
                <Chip
                  color="success"
                  sx={{ mt: 1, fontWeight: "bold", height: "20px" }}
                  label="Incluso"
                />
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h4" whiteSpace="nowrap">
                {formatter.format(Number(garanzia.prezzo) || 0)}
              </Typography>
              <IconButton onClick={onClose} aria-label="Chiudi" size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>

          {garanzia.descrizione && (
            <>
              <Divider />
              <Sezione
                title="Descrizione"
                rows={garanzia.descrizione}
                type="normal"
              />
            </>
          )}
          {details.informativa.length > 0 && (
            <>
              <Divider />
              <Sezione
                title="Informativa"
                rows={details.informativa}
                type="table"
              />
            </>
          )}

          {details.costi.length > 0 && (
            <>
              <Divider />
              <Sezione
                title={details.costiLabel}
                rows={details.costi}
                total={details.costiTotale}
                type="table"
              />
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default GaranziaDrawer;
